/**
 * O que a tela diz de um aviso (E66.F3; E150.F1 — GUS-150, ADR-0102 §4), no portal e na equipe.
 *
 * O servidor manda o TIPO e o fato; a frase sai do catálogo de i18n, com fallback neutro para o tipo
 * que esta versão não conhece (o código cru nunca chega ao usuário, F-E61.F3-02). A data é a data
 * civil que o SERVIDOR resolveu (F-E66.F2-02). Na conversa, o aviso diz também o que aconteceu: o
 * documento pedido, o que a mensagem é ou o desfecho da resposta.
 */
import { ptBR } from '@/i18n/pt-BR';
import type { AvisoData, AvisoTipo } from '@/lib/portal/avisos';

const tc = ptBR.conversaDoCaso;

/** Os textos de cada área: o portal (`portal.avisos`) e a equipe (`avisosDaEquipe`). */
export interface TextosDosAvisos {
  tipoIndefinido: string;
  detalheComCcb: string;
  detalheDoCaso: string;
  detalheComMais: string;
  contadorNaoVistos: string;
  contadorTudoLido: string;
  marcarVisto: string;
  marcado: string;
  marcarErro: string;
  abrirCaso: string;
  erroCarregar: string;
  vazioTitulo: string;
  vazioTexto: string;
}

const VARIANTE: Partial<Record<AvisoTipo, 'success' | 'info' | 'neutral' | 'warning'>> = {
  exame_certificado: 'success',
  laudo_homologado: 'info',
  laudo_gerado: 'neutral',
  documento_solicitado: 'warning',
  mensagem_da_equipe: 'info',
  mensagem_do_cliente: 'info',
  complemento_recebido: 'success',
  pedido_respondido: 'neutral',
};

export function varianteDoTipo(tipo: string): 'success' | 'info' | 'neutral' | 'warning' {
  return VARIANTE[tipo as AvisoTipo] ?? 'neutral';
}

/** Formata a data civil resolvida pelo servidor (`AAAA-MM-DD`). */
export function formatarDia(dataCivil: string): string {
  const [year, month, day] = dataCivil.split('-');
  return `${day}/${month}/${year}`;
}

/** Chave do aviso — a MESMA do servidor: o fato, não o caso. */
export function chaveDoAviso(aviso: { tipo: string; objeto_id: string }): string {
  return `${aviso.tipo}:${aviso.objeto_id}`;
}

/** O que aconteceu na conversa, em uma expressão: o documento pedido, o desfecho, o tipo. */
function oQueAconteceu(aviso: AvisoData): string | null {
  if (aviso.tipo === 'documento_solicitado') return aviso.material ?? null;
  if (aviso.tipo === 'pedido_respondido' && aviso.desfecho) return tc.situacao[aviso.desfecho];
  if (aviso.mensagem_tipo && aviso.tipo !== 'complemento_recebido') {
    return tc.tipo[aviso.mensagem_tipo];
  }
  return null;
}

export function detalheDoAviso(aviso: AvisoData, t: TextosDosAvisos): string {
  const base = aviso.ccb_number
    ? t.detalheComCcb.replace('{caso}', aviso.case_display_code).replace('{ccb}', aviso.ccb_number)
    : t.detalheDoCaso.replace('{caso}', aviso.case_display_code);
  const mais = oQueAconteceu(aviso);
  return mais === null ? base : t.detalheComMais.replace('{base}', base).replace('{mais}', mais);
}
